import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithSessionHandling } from '../utils/authFetch';
import { parseApiDateTime } from '../utils/apiDateTime';

type SortBy = 'loggedInAt' | 'user';
type SortOrder = 'asc' | 'desc';

interface LoginHistoryItem {
  id: number;
  userId: string;
  email: string;
  username: string;
  loggedInAt: string;
}

interface LoginHistoryResponse {
  items: LoginHistoryItem[];
  page: number;
  limit: number;
  total: number;
}

interface DeleteLoginHistoriesResponse {
  deletedCount: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const JST_TIME_ZONE = 'Asia/Tokyo';

const formatLoginDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const date = parseApiDateTime(value);
  if (!date) {
    return value;
  }

  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return `${partMap.year}/${partMap.month}/${partMap.day} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
};

const LoginHistoryView: React.FC = () => {
  const { token, logout } = useAuth();
  const [items, setItems] = useState<LoginHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortBy, setSortBy] = useState<SortBy>('loggedInAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [query, setQuery] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const fetchHistories = async (pageOverride?: number, showError = true) => {
    const authToken = token;
    const params = new URLSearchParams({
      sortBy,
      sortOrder,
      limit: String(rowsPerPage),
      page: String((pageOverride ?? page) + 1),
    });
    if (query) {
      params.set('query', query);
    }
    if (startDate) {
      params.set('startDate', startDate);
    }
    if (endDate) {
      params.set('endDate', endDate);
    }

    setLoading(true);
    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_BASE_URL}/api/admin/db/login-histories?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
        { logout },
      );
      if (sessionExpired) {
        return false;
      }
      if (!response.ok) {
        if (showError) {
          setSnackbar({ open: true, message: 'ログイン履歴の読み込みに失敗しました', severity: 'error' });
        }
        return false;
      }

      const data: LoginHistoryResponse = await response.json();
      setItems(data.items);
      setTotal(data.total);
      return true;
    } catch (error) {
      console.error('ログイン履歴の取得に失敗しました', error);
      if (showError) {
        setSnackbar({ open: true, message: 'ログイン履歴の読み込みに失敗しました', severity: 'error' });
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchHistories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortBy, sortOrder, query, startDate, endDate]);

  const handleSearch = () => {
    if (startDateInput && endDateInput && startDateInput > endDateInput) {
      setSnackbar({ open: true, message: '開始日は終了日以前の日付を指定してください', severity: 'error' });
      return;
    }
    setPage(0);
    setQuery(queryInput.trim());
    setStartDate(startDateInput);
    setEndDate(endDateInput);
  };

  const handleClear = () => {
    setQueryInput('');
    setQuery('');
    setStartDateInput('');
    setStartDate('');
    setEndDateInput('');
    setEndDate('');
    setSortBy('loggedInAt');
    setSortOrder('desc');
    setRowsPerPage(100);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleSortByChange = (event: SelectChangeEvent<SortBy>) => {
    setSortBy(event.target.value as SortBy);
    setPage(0);
  };

  const handleSortOrderChange = (event: SelectChangeEvent<SortOrder>) => {
    setSortOrder(event.target.value as SortOrder);
    setPage(0);
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDeleteAll = async () => {
    const authToken = token;

    setIsDeleting(true);
    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_BASE_URL}/api/admin/db/login-histories`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` },
        },
        { logout },
      );
      if (sessionExpired) {
        return;
      }
      if (!response.ok) {
        setSnackbar({ open: true, message: 'ログイン履歴の削除に失敗しました', severity: 'error' });
        return;
      }

      const data: DeleteLoginHistoriesResponse = await response.json();
      setIsDeleteDialogOpen(false);
      setPage(0);
      const refreshed = await fetchHistories(0, false);
      if (!refreshed) {
        setItems([]);
        setTotal(0);
      }
      setSnackbar({
        open: true,
        message: `ログイン履歴を${data.deletedCount}件削除しました`,
        severity: 'success',
      });
    } catch (error) {
      console.error('ログイン履歴の削除に失敗しました', error);
      setSnackbar({ open: true, message: 'ログイン履歴の削除に失敗しました', severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={2}
        sx={{ mb: 1 }}
      >
        <Typography variant="h4">
          ログイン履歴
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={handleOpenDeleteDialog}
          disabled={loading || isDeleting}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap' }}
        >
          履歴削除
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        各ユーザの保存件数は最新100件までです。表示時刻と日付絞り込みは日本時間です。
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        <TextField
          label="ユーザ検索"
          placeholder="メールアドレス・ユーザ名・ユーザID"
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSearch();
            }
          }}
          fullWidth
        />
        <TextField
          label="開始日"
          type="date"
          value={startDateInput}
          onChange={(event) => setStartDateInput(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <TextField
          label="終了日"
          type="date"
          value={endDateInput}
          onChange={(event) => setEndDateInput(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="login-history-sort-by-label">並び替え</InputLabel>
          <Select
            labelId="login-history-sort-by-label"
            value={sortBy}
            label="並び替え"
            onChange={handleSortByChange}
          >
            <MenuItem value="loggedInAt">日時順</MenuItem>
            <MenuItem value="user">ユーザ順</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="login-history-sort-order-label">順序</InputLabel>
          <Select
            labelId="login-history-sort-order-label"
            value={sortOrder}
            label="順序"
            onChange={handleSortOrderChange}
          >
            <MenuItem value="desc">降順</MenuItem>
            <MenuItem value="asc">昇順</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap', alignSelf: { xs: 'stretch', md: 'center' } }}>
          <Button variant="contained" onClick={handleSearch} sx={{ whiteSpace: 'nowrap', minWidth: 96 }}>
            絞り込み
          </Button>
          <Button variant="outlined" onClick={handleClear} sx={{ whiteSpace: 'nowrap', minWidth: 96 }}>
            クリア
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>メールアドレス</TableCell>
              <TableCell>ユーザ名</TableCell>
              <TableCell>ユーザID</TableCell>
              <TableCell>ログイン日時</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  該当するログイン履歴はありません
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.email}</TableCell>
                  <TableCell>{item.username}</TableCell>
                  <TableCell>{item.userId}</TableCell>
                  <TableCell>{formatLoginDateTime(item.loggedInAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[100, 500]}
        labelRowsPerPage="表示件数"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>ログイン履歴削除の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            保存されているログイン履歴をすべて削除します。この操作は元に戻せません。削除してもよろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained" disabled={isDeleting}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginHistoryView;
